import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FileText, Settings as SettingsIcon, ChevronRight, Calendar, Loader2, HeartPulse } from "lucide-react";
import AppShell from "@/components/AppShell";
import ActionCenter from "@/components/ActionCenter";
import { getSessionBusiness, getLatestBrief } from "@/lib/api";

function getNextMonday() {
  const now = new Date();
  const day = now.getDay();
  const diff = (8 - day) % 7 || 7;
  const nextMonday = new Date(now);
  nextMonday.setDate(now.getDate() + diff);
  nextMonday.setHours(7, 0, 0, 0);
  return nextMonday;
}

function useCountdown(target) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  const ms = Math.max(0, target - now);
  const days = Math.floor(ms / (1000 * 60 * 60 * 24));
  const hours = Math.floor((ms / (1000 * 60 * 60)) % 24);
  const minutes = Math.floor((ms / (1000 * 60)) % 60);
  const seconds = Math.floor((ms / 1000) % 60);
  return { days, hours, minutes, seconds };
}

function trialCopy(trialEndsAt) {
  if (!trialEndsAt) return null;
  const end = new Date(trialEndsAt);
  if (Number.isNaN(end.getTime())) return null;
  const ms = end - new Date();
  const days = Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)));
  if (ms <= 0) return "Your free Monday Brief trial has ended.";
  const label = days === 1 ? "day" : "days";
  return `Trial: ${days} ${label} left in your free Monday Brief trial.`;
}

function todayLabel() {
  return new Date().toLocaleDateString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
  });
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [business, setBusiness] = useState(null);
  const [loadState, setLoadState] = useState("loading"); // loading | ready | error
  const [latestBrief, setLatestBrief] = useState(null);
  const target = useMemo(() => getNextMonday(), []);
  const { days, hours, minutes, seconds } = useCountdown(target);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const biz = await getSessionBusiness();
        if (!alive) return;
        setBusiness(biz);
        setLoadState("ready");
        try {
          const brief = await getLatestBrief();
          if (alive) setLatestBrief(brief || null);
        } catch (_) {
          // Latest-brief failure is non-fatal: dashboard renders the
          // preparation state below.
        }
      } catch (err) {
        if (!alive) return;
        if (err?.response?.status === 401) {
          navigate("/signup", { replace: true });
          return;
        }
        setLoadState("error");
      }
    })();
    return () => { alive = false; };
  }, [navigate]);

  if (loadState === "loading") {
    return (
      <AppShell showTabBar activeTab="home" testId="dashboard-page">
        <div className="pt-12 flex items-center gap-2 text-base" style={{ color: "var(--ta-text-secondary)" }} data-testid="dashboard-loading">
          <Loader2 size={18} className="animate-spin" /> Loading your dashboard…
        </div>
      </AppShell>
    );
  }

  if (loadState === "error") {
    return (
      <AppShell showTabBar activeTab="home" testId="dashboard-page">
        <div className="pt-12 max-w-lg" data-testid="dashboard-error">
          <div className="font-display text-2xl font-semibold" style={{ color: "var(--ta-primary)" }}>
            We couldn't load this information right now.
          </div>
          <p className="mt-2 text-base" style={{ color: "var(--ta-text-secondary)" }}>
            Please try again shortly.
          </p>
          <button
            onClick={() => window.location.reload()}
            data-testid="dashboard-error-retry"
            className="mt-6 min-h-[56px] px-6 rounded-2xl text-base font-medium text-white"
            style={{ background: "var(--ta-primary)" }}
          >
            Try again
          </button>
        </div>
      </AppShell>
    );
  }

  const ownerName = (business?.ownerFirstName || "").trim();
  const businessName = (business?.businessName || "").trim();
  const trialText = trialCopy(business?.trialEndsAt);

  const briefEyebrow = latestBrief ? "Your latest Monday Brief" : "Coming next Monday";
  const briefTitle = latestBrief ? "Latest Monday Brief" : "Your first Monday Brief";
  const briefDesc = latestBrief
    ? "Open your most recent Monday Brief."
    : "We're preparing your first Monday Brief. It will appear here after it has been generated.";

  return (
    <AppShell showTabBar activeTab="home" testId="dashboard-page">
      <div className="pt-6 sm:pt-10 stagger">
        <div className="text-xs uppercase tracking-widest font-medium mb-2" style={{ color: "var(--ta-secondary)" }} data-testid="dashboard-date">
          {todayLabel()}
        </div>
        <h1
          className="font-display text-4xl sm:text-5xl font-semibold tracking-tight leading-[1.05]"
          style={{ color: "var(--ta-primary)" }}
          data-testid="dashboard-greeting"
        >
          {ownerName ? `Good morning, ${ownerName}.` : (businessName ? `Welcome, ${businessName}.` : "Good morning.")}
        </h1>
        <p className="mt-3 text-lg leading-relaxed max-w-md" style={{ color: "var(--ta-text-secondary)" }} data-testid="dashboard-subhead">
          We're reviewing your business information. Your first Monday Brief lands next Monday at 7:00am.
        </p>

        {/* Countdown card — next Monday Brief */}
        <div
          className="mt-8 relative overflow-hidden rounded-[32px] p-6 sm:p-8 text-white"
          style={{ background: "var(--ta-primary)" }}
          data-testid="countdown-card"
        >
          <div className="flex items-center gap-2 text-white/80 text-sm">
            <Calendar size={16} strokeWidth={2.2} />
            Next Monday Brief
          </div>
          <div className="mt-4 grid grid-cols-4 gap-3 sm:gap-6">
            <CountUnit label="days" value={days} testId="countdown-days" />
            <CountUnit label="hrs" value={hours} testId="countdown-hours" />
            <CountUnit label="min" value={minutes} testId="countdown-minutes" />
            <CountUnit label="sec" value={seconds} testId="countdown-seconds" />
          </div>
          <p className="mt-6 text-white/80 text-sm max-w-sm">
            Every Monday at 7:00am we'll send you one clear read on last week — plus what to do this week.
          </p>
        </div>

        {/* Action Center tiles — scoped to the current session's business */}
        <ActionCenter />

        {/* Primary nav cards */}
        <div className="mt-8 flex flex-col gap-4" data-testid="dashboard-nav">
          <NavCard
            to="/brief"
            testId="nav-monday-brief"
            eyebrow={briefEyebrow}
            title={briefTitle}
            desc={briefDesc}
            icon={FileText}
            tint="primary"
          />
          <NavCard
            to="/health"
            testId="nav-business-health"
            eyebrow="At a glance"
            title="Business Health"
            desc="See what we're reviewing about your business."
            icon={HeartPulse}
            tint="accent"
          />
          <NavCard
            to="/settings"
            testId="nav-settings"
            eyebrow="Preferences"
            title="Settings"
            desc="Update your business details and notification preferences."
            icon={SettingsIcon}
            tint="soft"
          />
        </div>

        {trialText && (
          <p className="mt-10 text-sm" style={{ color: "var(--ta-text-secondary)" }} data-testid="dashboard-trial">
            {trialText}
          </p>
        )}
      </div>
    </AppShell>
  );
}

const CountUnit = ({ label, value, testId }) => (
  <div className="text-center">
    <div className="font-display text-4xl sm:text-5xl font-semibold tabular-nums" data-testid={testId}>
      {String(value).padStart(2, "0")}
    </div>
    <div className="text-[10px] uppercase tracking-widest text-white/60 mt-1">{label}</div>
  </div>
);

const NavCard = ({ to, testId, eyebrow, title, desc, icon: Icon, tint }) => {
  const bg = tint === "primary" ? "#fff" : tint === "accent" ? "var(--ta-accent)" : "var(--ta-surface-alt)";
  const iconBg = "var(--ta-primary)";
  const iconColor = "#fff";
  return (
    <Link
      to={to}
      data-testid={testId}
      className="block p-6 rounded-[24px] shadow-sm border active:scale-[0.99] transition-transform"
      style={{ background: bg, borderColor: "var(--ta-border)" }}
    >
      <div className="flex items-start gap-4">
        <div
          className="w-12 h-12 shrink-0 rounded-2xl inline-flex items-center justify-center"
          style={{ background: iconBg, color: iconColor }}
        >
          <Icon size={22} strokeWidth={2.2} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs uppercase tracking-widest font-medium mb-1" style={{ color: "var(--ta-secondary)" }}>{eyebrow}</div>
          <div className="font-display font-semibold text-xl" style={{ color: "var(--ta-primary)" }}>{title}</div>
          <div className="text-base mt-1 leading-snug" style={{ color: "var(--ta-text-secondary)" }}>{desc}</div>
        </div>
        <ChevronRight size={22} className="shrink-0" style={{ color: "var(--ta-primary)" }} />
      </div>
    </Link>
  );
};
